/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, July 7th 2022, 9:41:03 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import WorkspacesIcon from "@mui/icons-material/Workspaces";
import { useTranslatedText } from "core";
import { ProcessStage, STAGE_TYPES } from "lib/schemas";
import { getNewProcessStageDefault } from "lib/factories";
import { useProcessStageAPIAPIForm, UseProcessStageAPIFormSpec } from "service-builder/services";
import { useServiceBuilderContext } from "service-builder/providers";
import { TranslatableTextFormInput } from "translation/components";
import {
  OSAPIForm,
  TextFormInput,
  SwitchFormInput,
  ListButtonAutoCompleteFormInput,
  FormSubmitBubblingStopper,
} from "ui-widgets/forms";
import { IconText, StyledOSDialog, InputHelpDialogWrapper } from "ui-widgets";
import { StageIcons } from "./StageWidgets";

interface PopUpStageFormProps extends UseProcessStageAPIFormSpec {
  open: boolean;
  handleClose: () => void;
}

export const PopUpStageForm = ({ open, handleClose, ...formProps }: PopUpStageFormProps) => {
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const newStageDefault = getNewProcessStageDefault({ processDefinition: currentProcessDefinition, sequence: 0 });

  const LANG = useTranslatedText();

  return (
    <StyledOSDialog
      open={open}
      onClose={handleClose}
      popUpTitle="Workflow Stage"
      popUpIcon={<WorkspacesIcon color="primary" />}
      popUpDescription={LANG.stageSettingsDescription}
    >
      <StageForm
        {...formProps}
        onSuccessfulAPISave={() => {
          formProps?.onSuccessfulAPISave?.();
          handleClose();
        }}
        processStageDefaultValues={newStageDefault}
      />
    </StyledOSDialog>
  );
};

const StageForm = ({
  processStageDefaultValues,
  processStageFetchId = null,
  updateProcessStageFromAPICallback,
  onSuccessfulAPISave,
}: UseProcessStageAPIFormSpec) => {
  const useAPIFormReturn = useProcessStageAPIAPIForm({
    processStageFetchId,
    processStageDefaultValues,
    updateProcessStageFromAPICallback,
    onSuccessfulAPISave,
  });

  return (
    <FormSubmitBubblingStopper>
      <OSAPIForm useAPIFormReturn={useAPIFormReturn} loaderType="form-skeleton">
        <TranslatableTextFormInput<ProcessStage> schema_field_key="stage_name" label="Stage Name" />
        <ListButtonAutoCompleteFormInput<ProcessStage>
          schema_field_key="stage_type"
          label="Stage Type"
          options={Object.keys(STAGE_TYPES)}
          getOptionLabel={(option) => STAGE_TYPES[option]} // For the TextField
          getPrimaryOptionLabel={(option) => <IconText icon={StageIcons[option]} textComponent={STAGE_TYPES[option]} />}
          getSecondaryOptionLabel={(option) => null}
        />
        <InputHelpDialogWrapper
          helpTitle={null}
          helpContent={<>The progress of the workflow process that this stage represents.</>}
        >
          <TextFormInput<ProcessStage>
            schema_field_key="progress_percentage"
            label="Percentage Progress"
            type="number"
          />
        </InputHelpDialogWrapper>

        <SwitchFormInput<ProcessStage>
          schema_field_key="is_landing"
          label="Landing Stage"
          helperText="Is this the default stage you want users to land on when they view a list of stages?"
        />
        <SwitchFormInput<ProcessStage>
          schema_field_key="active"
          label="Active"
          helperText="Activate or Deactivate this stage."
        />
      </OSAPIForm>
    </FormSubmitBubblingStopper>
  );
};
